import { printPixels, type Product } from '../design/products';

// The poster is laid out once, at roughly 900 CSS px tall. The editor scales that layout to
// fit the screen with a CSS transform; print renders the same DOM at a higher device pixel
// ratio. Nothing is re-laid-out between the two, which is what makes the print match the preview.
const TARGET_H = 900;
export const MARGIN = 20;
export const FRAME = 3;
export const PANEL_H = 183;
export const PROFILE_H = 100;

export interface PosterLayout {
  /** Poster size in CSS px: whole numbers in exactly the print file's aspect ratio. */
  width: number;
  height: number;
  /** Map viewport (inside the frame lines), relative to the poster. */
  map: { x: number; y: number; width: number; height: number };
  /** Print file size, and the device pixel ratio that produces it from this layout. */
  print: { width: number; height: number; scale: number };
}

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

export function posterLayout(product: Pick<Product, 'size' | 'framing'>): PosterLayout {
  const print = printPixels(product);
  // Whole CSS pixels in the print's exact ratio, as close to 900 tall as that allows
  // (e.g. 2475x3675 -> 594x882). Print pixels then map 1:1 with no rounding or cropping.
  const g = gcd(print.width, print.height);
  const k = Math.max(1, Math.round(TARGET_H / (print.height / g)));
  const width = (print.width / g) * k;
  const height = (print.height / g) * k;
  return {
    width,
    height,
    map: {
      x: MARGIN + FRAME,
      y: MARGIN + FRAME,
      width: width - 2 * (MARGIN + FRAME),
      height: height - 2 * MARGIN - PANEL_H - FRAME,
    },
    print: { ...print, scale: print.height / height },
  };
}

/** Space kept clear around the route when auto-fitting it into the map. */
export const fitPadding = (showElevation: boolean) => ({
  top: 44,
  right: 36,
  bottom: (showElevation ? PROFILE_H : 0) + 36,
  left: 36,
});
